import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Stack,
  Chip,
} from "@mui/material";
import {
  Search,
  Download,
  ArrowBack,
  Login,
  PeopleAlt,
  Person,
  Repeat,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import adminService from "../../redux/admin/adminService";
import { colors } from "../../constants/colors";

interface UserRow {
  name: string;
  aumUserId: string;
  accountType: string;
  loginCount: number;
  lastLoginAt: string;
}

interface SummaryData {
  totalLogins: number;
  uniqueUsersLoggedIn: number;
  usersWithOnlyOneLogin: number;
  usersWithMultipleLogins: number;
}

const UserLogins: React.FC = () => {
  const navigate = useNavigate();
  const [dateType, setDateType] = useState<string>("7day");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [summary, setSummary] = useState<SummaryData>({
    totalLogins: 0,
    uniqueUsersLoggedIn: 0,
    usersWithOnlyOneLogin: 0,
    usersWithMultipleLogins: 0,
  });
  const [usersList, setUsersList] = useState<UserRow[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [tabValue, setTabValue] = useState(0);

  const cacheRef = useRef<Record<string, { summary: SummaryData; usersList: UserRow[] }>>({});

  const getDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();

    switch (dateType) {
      case "1day":
        start.setHours(0, 0, 0, 0);
        break;
      case "7day":
        start.setDate(end.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        break;
      case "1month":
        start.setMonth(end.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "1year":
        start.setFullYear(end.getFullYear() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "max":
        start.setFullYear(2000, 0, 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "custom":
        if (customStartDate && customEndDate) {
          const customStart = new Date(customStartDate);
          customStart.setHours(0, 0, 0, 0);
          const customEnd = new Date(customEndDate);
          customEnd.setHours(23, 59, 59, 999);
          return { startDate: customStart.getTime(), endDate: customEnd.getTime() };
        }
        return null;
      default:
        start.setDate(end.getDate() - 7);
        start.setHours(0, 0, 0, 0);
    }

    return { startDate: start.getTime(), endDate: end.getTime() };
  }, [dateType, customStartDate, customEndDate]);

  const fetchData = useCallback(async (isCustomSubmit = false) => {
    if (dateType === "custom" && !isCustomSubmit) return;

    const range = getDateRange();
    if (!range) return;

    const cacheKey = dateType === "custom" ? `custom_${range.startDate}_${range.endDate}` : dateType;

    if (cacheRef.current[cacheKey]) {
      setSummary(cacheRef.current[cacheKey].summary);
      setUsersList(cacheRef.current[cacheKey].usersList);
      return;
    }

    setLoading(true);
    try {
      const response = await adminService.getSuperAdminOpsUserLogins({
        startDate: range.startDate,
        endDate: range.endDate,
      });

      if (response && (response.statusCode === 200 || response.success === 200)) {
        const newSummary = {
          totalLogins: Number(response.data.totalLogins || 0),
          uniqueUsersLoggedIn: Number(response.data.uniqueUsersLoggedIn || 0),
          usersWithOnlyOneLogin: Number(response.data.usersWithOnlyOneLogin || 0),
          usersWithMultipleLogins: Number(response.data.usersWithMultipleLogins || 0),
        };
        const newUsers = response.data.users || [];

        setSummary(newSummary);
        setUsersList(newUsers);
        cacheRef.current[cacheKey] = { summary: newSummary, usersList: newUsers };
      } else {
        toast.error(response.message || "Failed to fetch login data");
      }
    } catch (error: any) {
      console.error("Error fetching user logins:", error);
      toast.error(error.message || "Failed to load user logins");
    } finally {
      setLoading(false);
    }
  }, [getDateRange, dateType]);

  useEffect(() => {
    if (dateType !== "custom") {
      fetchData();
    }
  }, [dateType, fetchData]);

  const handleCustomApply = () => {
    if (!customStartDate || !customEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    fetchData(true);
  };

  const handleExport = () => {
    if (displayedUsers.length === 0) {
      toast.error("No data to export");
      return;
    }

    const exportData = displayedUsers.map((user) => ({
      Name: user.name,
      "AUM User ID": user.aumUserId,
      "Account Type": user.accountType,
      "Login Count": user.loginCount,
      "Last Login": user.lastLoginAt,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "User Logins Report");
    XLSX.writeFile(workbook, `User_Logins_${dateType}_${Date.now()}.xlsx`);
  };

  const filteredUsers = usersList.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.aumUserId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allUsers = filteredUsers;
  const singleLoginUsers = filteredUsers.filter((u) => u.loginCount === 1);
  const multipleLoginUsers = filteredUsers.filter((u) => u.loginCount >= 2);

  const displayedUsers =
    tabValue === 0 ? allUsers : tabValue === 1 ? singleLoginUsers : multipleLoginUsers;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/super-admin/ops-dashboard");
            }}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <ArrowBack sx={{ mr: 0.5, fontSize: "inherit" }} />
            Ops Dashboard
          </Link>
          <Typography color="text.primary">User Logins</Typography>
        </Breadcrumbs>

        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary }}>
          User Login Activity
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          View investor login counts and activity for the selected period.
        </Typography>
      </Box>

      <Card sx={{ mb: 4, borderRadius: "12px", border: "1px solid #e8e8e8", boxShadow: "none" }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "text.secondary" }}>
                Select Range Type
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {[
                  { value: "1day", label: "1 Day" },
                  { value: "7day", label: "7 Days" },
                  { value: "1month", label: "1 Month" },
                  { value: "1year", label: "1 Year" },
                  { value: "max", label: "Max" },
                  { value: "custom", label: "Custom Range" },
                ].map((type) => (
                  <Button
                    key={type.value}
                    variant={dateType === type.value ? "contained" : "outlined"}
                    onClick={() => setDateType(type.value)}
                    sx={{
                      borderRadius: "20px",
                      textTransform: "none",
                      px: 3,
                      fontWeight: 600,
                    }}
                  >
                    {type.label}
                  </Button>
                ))}
              </Stack>
            </Grid>

            {dateType === "custom" && (
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                  Custom Range Dates
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    size="small"
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    onClick={handleCustomApply}
                    sx={{ fontWeight: 600, minWidth: "90px" }}
                    disabled={loading || !customStartDate || !customEndDate}
                  >
                    Apply
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px", height: "100%" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                  Total Logins
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: colors.primary }}>
                  {loading ? <CircularProgress size={24} /> : summary.totalLogins}
                </Typography>
              </Box>
              <Login sx={{ fontSize: 32, color: colors.primary, opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px", height: "100%" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                  Users Logged In
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: "#0891B2" }}>
                  {loading ? <CircularProgress size={24} /> : summary.uniqueUsersLoggedIn}
                </Typography>
              </Box>
              <PeopleAlt sx={{ fontSize: 32, color: "#0891B2", opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px", height: "100%" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                  Only 1 Login
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: "#F17A21" }}>
                  {loading ? <CircularProgress size={24} /> : summary.usersWithOnlyOneLogin}
                </Typography>
              </Box>
              <Person sx={{ fontSize: 32, color: "#F17A21", opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px", height: "100%" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                  2+ Logins
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: "#08991F" }}>
                  {loading ? <CircularProgress size={24} /> : summary.usersWithMultipleLogins}
                </Typography>
              </Box>
              <Repeat sx={{ fontSize: 32, color: "#08991F", opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e8e8e8", boxShadow: "none" }}>
        <Box sx={{ p: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <TextField
            placeholder="Search by Name or AUM User ID..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: "100%", sm: "300px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExport}
            disabled={loading || displayedUsers.length === 0}
            sx={{ fontWeight: 600 }}
          >
            Export Excel
          </Button>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_e, newValue) => setTabValue(newValue)}
            aria-label="login filter tabs"
          >
            <Tab label={`All (${allUsers.length})`} sx={{ fontWeight: 600 }} />
            <Tab label={`Only 1 Login (${singleLoginUsers.length})`} sx={{ fontWeight: 600 }} />
            <Tab label={`2+ Logins (${multipleLoginUsers.length})`} sx={{ fontWeight: 600 }} />
          </Tabs>
        </Box>

        <TableContainer sx={{ maxHeight: "550px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Investor Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  AUM User ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Account Type
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Login Count
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Last Login
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography mt={2} color="textSecondary">
                      Fetching login activity records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : displayedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <Typography color="textSecondary" fontSize={15}>
                      No login records found for the selected period.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedUsers.map((user) => (
                  <TableRow key={user.aumUserId} hover>
                    <TableCell sx={{ py: 2, fontWeight: 600 }}>{user.name}</TableCell>
                    <TableCell sx={{ py: 2 }}>{user.aumUserId}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={user.accountType}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: user.accountType === "Individual" ? "success.light" : "info.light",
                          color: user.accountType === "Individual" ? "success.dark" : "info.dark",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, fontWeight: 600 }}>{user.loginCount}</TableCell>
                    <TableCell sx={{ py: 2 }}>{user.lastLoginAt}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default UserLogins;
